import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { purchaseOrderAPI, propertyAPI, storeAPI, vendorAPI, itemAPI, unitAPI, clientAPI, userAPI, serviceItemAPI, servicePOAPI, categoryAPI, getBillUrl, downloadBill, viewBill } from '../lib/api';
import { compressImageFile } from '../lib/imageCompress';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { DataTable } from '../components/DataTable';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Plus, 
  AlertTriangle,
  Pencil, 
  Trash2, 
  FileText, 
  Loader2, 
  Download, 
  CheckCircle, 
  XCircle,
  Eye,
  ShoppingCart,
  X,
  Truck,
  IndianRupee,
  Calendar,
  UserCircle,
  Users,
  CreditCard,
  Search,
  Upload,
  RefreshCw,
  Wrench,
  Briefcase,
  ClipboardCheck
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, formatCurrency } from '../lib/utils';
import { SearchableItemSelect } from '../components/SearchableItemSelect';

// Use relative URLs for production compatibility - always use empty string for deployed environments
const BACKEND_URL = '';

const getStatusColor = (status) => {
  switch (status) {
    case 'draft': return 'bg-gray-100 text-gray-700';
    case 'approved': return 'bg-green-100 text-green-700';
    case 'cancelled': return 'bg-red-100 text-red-700';
    case 'completed': return 'bg-blue-100 text-blue-700';
    default: return 'bg-gray-100 text-gray-700';
  }
};

export const PurchaseOrdersPage = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [properties, setProperties] = useState([]);
  const [stores, setStores] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [items, setItems] = useState([]);
  const [serviceItems, setServiceItems] = useState([]);
  const [units, setUnits] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPaymentStatus, setFilterPaymentStatus] = useState('all');
  const [filterClient, setFilterClient] = useState('all');
  const [filterCreatedBy, setFilterCreatedBy] = useState('all');
  const [filterVendor, setFilterVendor] = useState('all');
  const [filterGRN, setFilterGRN] = useState('all');
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(null);
  
  // Payment dialog state
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentFormData, setPaymentFormData] = useState({
    payment_made: '',
    committed_payment_date: '',
    payment_notes: '',
    payment_made_by: '',
    payment_mode: '',
    payment_date: '',
    payment_screenshot: '',
    payment_screenshots: []
  });
  const [uploadingScreenshot, setUploadingScreenshot] = useState(false);
  const paymentScreenshotRef = React.useRef(null);
  
  // Payment mode options
  const paymentModes = ['Cash', 'UPI', 'Online', 'Bank Transfer', 'Cheque'];
  
  // Bulk selection state
  const [selectedPOs, setSelectedPOs] = useState([]);
  const [bulkAssignDialogOpen, setBulkAssignDialogOpen] = useState(false);
  const [bulkClientId, setBulkClientId] = useState('');
  
  // Super Admin check
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [recalculating, setRecalculating] = useState(null);
  const [recalculatingAll, setRecalculatingAll] = useState(false);
  const [poGrnCount, setPoGrnCount] = useState(0);
  const poHasGrn = poGrnCount > 0;
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [downloadingReport, setDownloadingReport] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    property_id: '',
    store_id: '',
    vendor_id: '',
    custom_vendor_name: '',
    expected_delivery_date: '',
    shipping_address: '',
    billing_address: '',
    terms_conditions: '',
    notes: '',
    items: [],
    client_id: '',
    client_ids: [],
    committed_payment_date: '',
    po_type: 'goods',  // "goods" or "service"
    adjustment: 0,  // Adjustment amount for Super Admin
    adjustment_notes: ''  // Notes for adjustment
  });
  
  // Track if using custom vendor
  const [useCustomVendor, setUseCustomVendor] = useState(false);

  // Item form state (supports both goods and services)
  const [currentItem, setCurrentItem] = useState({
    item_id: '',
    description: '',  // For service POs
    quantity: '',
    unit_id: '',
    unit: '',  // Free text unit for services
    rate: '',
  });

  const [filteredStores, setFilteredStores] = useState([]);

  // Create New Item Dialog State
  const [createItemDialogOpen, setCreateItemDialogOpen] = useState(false);
  const [savingItem, setSavingItem] = useState(false);
  const [categories, setCategories] = useState([]);
  const [newItemData, setNewItemData] = useState({
    name: '',
    code: '',
    category_id: '',
    default_unit_id: '',
    purchase_price: '',
    description: ''
  });

  useEffect(() => {
    fetchData();
    
    // Check if user is Super Admin
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        setIsSuperAdmin(user?.role_name === 'Super Admin');
      }
    } catch (e) {
      console.error('Error parsing user from localStorage:', e);
    }
  }, []);

  useEffect(() => {
    if (formData.property_id) {
      setFilteredStores(stores.filter(s => s.property_id === formData.property_id));
    } else {
      setFilteredStores([]);
    }
  }, [formData.property_id, stores]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [ordersRes, propertiesRes, storesRes, vendorsRes, itemsRes, unitsRes, clientsRes, usersRes, categoriesRes] = await Promise.all([
        purchaseOrderAPI.getAll(),
        propertyAPI.getAll({ status: 'active' }),
        storeAPI.getAll({ status: 'active' }),
        vendorAPI.getAll({ status: 'active' }),
        itemAPI.getAll({ status: 'active' }),
        unitAPI.getAll({ status: 'active' }),
        clientAPI.getAll({ status: 'active' }),
        userAPI.getAll({ status: 'active' }),
        categoryAPI.getAll({ status: 'active' })
      ]);
      setOrders(ordersRes.data);
      setProperties(propertiesRes.data);
      setStores(storesRes.data);
      setVendors(vendorsRes.data);
      setItems(itemsRes.data);
      setUnits(unitsRes.data);
      setClients(clientsRes.data);
      setUsers(usersRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleRecalculateBalance = async (order) => {
    try {
      setRecalculating(order.id);
      const response = await purchaseOrderAPI.recalculateBalance(order.id);
      toast.success(`Balance recalculated: ${response.data.payment_status}, Balance: ₹${response.data.balance_due}`);
      fetchData(); // Refresh the list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to recalculate balance');
    } finally {
      setRecalculating(null);
    }
  };

  const handleRepairFromGRN = async (order) => {
    try {
      setRecalculating(order.id);
      const response = await purchaseOrderAPI.repairFromGRN(order.id);
      toast.success(`PO repaired! Items: ${response.data.items_count}, Total: ₹${response.data.total_amount}, Balance: ₹${response.data.balance_due}`);
      fetchData(); // Refresh the list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to repair PO from GRN');
    } finally {
      setRecalculating(null);
    }
  };

  const handleRecalculateAll = async () => {
    if (!window.confirm('This will recalculate balance for ALL Purchase Orders. Continue?')) {
      return;
    }
    try {
      setRecalculatingAll(true);
      const response = await purchaseOrderAPI.recalculateAll();
      toast.success(`Recalculated ${response.data.updated} PO(s) with GRNs, synced ${response.data.synced_no_grn} without GRN.`);
      fetchData(); // Refresh the list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to recalculate all POs');
    } finally {
      setRecalculatingAll(false);
    }
  };

  const handleOpenDialog = async (order = null) => {
    if (order) {
      // Fetch the full PO (list items omit grn_count/actual_amount/items detail)
      let full = order;
      try {
        const res = await purchaseOrderAPI.getById(order.id);
        full = res.data;
      } catch { /* fall back to list item */ }
      setSelectedOrder(full);
      setPoGrnCount(full.grn_count || 0);
      const isCustomVendor = full.custom_vendor_name && !full.vendor_id;
      setUseCustomVendor(isCustomVendor);
      setFormData({
        property_id: full.property_id || '',
        store_id: full.store_id || '',
        vendor_id: full.vendor_id || '',
        custom_vendor_name: full.custom_vendor_name || '',
        expected_delivery_date: full.expected_delivery_date?.split('T')[0] || '',
        shipping_address: full.shipping_address || '',
        billing_address: full.billing_address || '',
        terms_conditions: full.terms_conditions || '',
        notes: full.notes || '',
        items: full.items || [],
        client_id: full.client_id || '',
        client_ids: full.client_ids && full.client_ids.length > 0 ? full.client_ids : (full.client_id ? [full.client_id] : []),
        committed_payment_date: full.committed_payment_date?.split('T')[0] || '',
        po_type: full.po_type || 'goods',
        adjustment: full.adjustment || 0,
        adjustment_notes: full.adjustment_notes || ''
      });
    } else {
      setSelectedOrder(null);
      setPoGrnCount(0);
      setUseCustomVendor(false);
      setFormData({
        property_id: '',
        store_id: '',
        vendor_id: '',
        custom_vendor_name: '',
        expected_delivery_date: '',
        shipping_address: '',
        billing_address: '',
        terms_conditions: '',
        notes: '',
        items: [],
        client_id: '',
        client_ids: [],
        committed_payment_date: '',
        po_type: 'goods',
        adjustment: 0,
        adjustment_notes: ''
      });
    }
    setCurrentItem({ item_id: '', description: '', quantity: '', unit_id: '', unit: '', rate: '' });
    setDialogOpen(true);
  };

  // Payment dialog handlers
  const handleOpenPaymentDialog = async (order) => {
    setSelectedOrder(order);
    setPaymentHistory(order.payment_history || []);
    setPaymentFormData({
      payment_made: '',
      committed_payment_date: order.committed_payment_date?.split('T')[0] || '',
      payment_notes: '',
      payment_made_by: '',
      payment_mode: '',
      payment_date: new Date().toISOString().split('T')[0],
      payment_screenshot: '',
      payment_screenshots: []
    });
    setPaymentDialogOpen(true);
    // Fetch the full PO so we have the latest balance + complete payment history
    try {
      const res = await purchaseOrderAPI.getById(order.id);
      const full = res.data;
      setSelectedOrder(full);
      setPaymentHistory(full.payment_history || []);
    } catch (e) {
      // keep list data as fallback
    }
  };

  // Handle payment screenshot upload (supports multiple)
  const handlePaymentScreenshotUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    try {
      setUploadingScreenshot(true);
      const newUrls = [];
      for (const file of files) {
        const toUpload = await compressImageFile(file);
        const formData = new FormData();
        formData.append('file', toUpload);
        const response = await fetch(`/api/upload/payment-screenshot`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
          body: formData
        });
        if (!response.ok) throw new Error('Upload failed');
        const data = await response.json();
        newUrls.push(data.url);
      }
      setPaymentFormData(prev => ({
        ...prev,
        payment_screenshots: [...(prev.payment_screenshots || []), ...newUrls],
        payment_screenshot: (prev.payment_screenshots?.[0]) || newUrls[0]
      }));
      toast.success(`${newUrls.length} screenshot(s) uploaded`);
    } catch (error) {
      toast.error('Failed to upload screenshot');
    } finally {
      setUploadingScreenshot(false);
      if (paymentScreenshotRef.current) {
        paymentScreenshotRef.current.value = '';
      }
    }
  };

  const removePaymentScreenshot = (index) => {
    setPaymentFormData(prev => {
      const next = (prev.payment_screenshots || []).filter((_, i) => i !== index);
      return { ...prev, payment_screenshots: next, payment_screenshot: next[0] || '' };
    });
  };

  const handleSavePayment = async () => {
    if (!selectedOrder) return;
    const amount = parseFloat(paymentFormData.payment_made);
    if (!amount || amount <= 0) {
      toast.error('Enter a valid payment amount');
      return;
    }
    try {
      setSaving(true);
      const res = await purchaseOrderAPI.recordPayment(selectedOrder.id, {
        amount,
        payment_date: paymentFormData.payment_date || null,
        payment_mode: paymentFormData.payment_mode || null,
        payment_made_by: paymentFormData.payment_made_by || null,
        payment_notes: paymentFormData.payment_notes || null,
        payment_screenshots: paymentFormData.payment_screenshots || []
      });
      toast.success('Payment recorded successfully');
      // Refresh history + balance within the dialog
      setPaymentHistory(res.data.payment_history || []);
      setSelectedOrder(prev => prev ? {
        ...prev,
        payment_made: res.data.payment_made,
        balance_due: res.data.balance_due,
        payment_status: res.data.payment_status
      } : prev);
      // Reset entry fields for the next payment
      setPaymentFormData(prev => ({
        ...prev,
        payment_made: '',
        payment_notes: '',
        payment_mode: '',
        payment_screenshots: [],
        payment_screenshot: '',
        payment_date: new Date().toISOString().split('T')[0]
      }));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!selectedOrder) return;
    if (!window.confirm('Remove this payment entry? The PO balance will be recalculated.')) return;
    try {
      const res = await purchaseOrderAPI.deletePayment(selectedOrder.id, paymentId);
      toast.success('Payment entry removed');
      setPaymentHistory(res.data.payment_history || []);
      setSelectedOrder(prev => prev ? {
        ...prev,
        payment_made: res.data.payment_made,
        balance_due: res.data.balance_due,
        payment_status: res.data.payment_status
      } : prev);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove payment');
    }
  };

  const handleDownloadPaymentReport = async () => {
    if (!selectedOrder) return;
    try {
      setDownloadingReport(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/purchase-orders/${selectedOrder.id}/payment-report-pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to download report');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `payment_report_${selectedOrder.po_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Payment report downloaded');
    } catch (error) {
      toast.error('Failed to download payment report');
    } finally {
      setDownloadingReport(false);
    }
  };

  // Bulk selection handlers
  const handleSelectPO = (poId, checked) => {
    if (checked) {
      setSelectedPOs([...selectedPOs, poId]);
    } else {
      setSelectedPOs(selectedPOs.filter(id => id !== poId));
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedPOs(filteredOrders.map(o => o.id));
    } else {
      setSelectedPOs([]);
    }
  };

  // Download Summary PDF for selected POs
  const handleDownloadSummaryPDF = async () => {
    if (selectedPOs.length === 0) {
      toast.error('Please select at least one PO');
      return;
    }
    
    try {
      setDownloading('summary');
      const response = await purchaseOrderAPI.downloadSummaryPDF(selectedPOs);
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `PO_Summary_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Downloaded summary for ${selectedPOs.length} POs`);
    } catch (error) {
      console.error('Error downloading summary:', error);
      toast.error('Failed to download summary PDF');
    } finally {
      setDownloading(null);
    }
  };

  const handleBulkAssignClient = async () => {
    if (!bulkClientId || selectedPOs.length === 0) {
      toast.error('Please select a client and at least one PO');
      return;
    }
    
    try {
      setSaving(true);
      await purchaseOrderAPI.bulkAssignClient({
        po_ids: selectedPOs,
        client_id: bulkClientId
      });
      toast.success(`${selectedPOs.length} POs assigned to client`);
      setBulkAssignDialogOpen(false);
      setBulkClientId('');
      setSelectedPOs([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign POs');
    } finally {
      setSaving(false);
    }
  };

  const handleBulkApprove = async () => {
    if (selectedPOs.length === 0) {
      toast.error('Please select at least one PO to approve');
      return;
    }
    
    // Filter only draft POs that can be approved
    const draftPOs = orders.filter(o => selectedPOs.includes(o.id) && o.status === 'draft');
    
    if (draftPOs.length === 0) {
      toast.error('No draft POs selected. Only draft POs can be approved.');
      return;
    }
    
    try {
      setSaving(true);
      const results = await purchaseOrderAPI.bulkApprove(draftPOs.map(po => po.id));
      
      const successCount = results.data.success_count || draftPOs.length;
      const failedCount = results.data.failed_count || 0;
      
      if (failedCount > 0) {
        toast.warning(`${successCount} POs approved, ${failedCount} failed`);
      } else {
        toast.success(`${successCount} POs approved successfully`);
      }
      
      setSelectedPOs([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve POs');
    } finally {
      setSaving(false);
    }
  };

  // Get payment status badge color
  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-700';
      case 'partial': return 'bg-yellow-100 text-yellow-700';
      case 'unpaid': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedOrder(null);
  };

  const handleAddItem = () => {
    const isServicePO = formData.po_type === 'service';
    
    // Different validation for goods vs services
    if (isServicePO) {
      if (!currentItem.description || !currentItem.quantity || !currentItem.rate || !currentItem.unit_id) {
        toast.error('Please fill description, quantity, unit, and rate');
        return;
      }
    } else {
      if (!currentItem.item_id || !currentItem.quantity || !currentItem.rate) {
        toast.error('Please fill item, quantity, and rate');
        return;
      }
    }

    const qty = parseFloat(currentItem.quantity);
    const rate = parseFloat(currentItem.rate);

    let newItem;
    if (isServicePO) {
      // Service item - use description and unit from units master
      const unit = units.find(u => u.id === currentItem.unit_id);
      newItem = {
        description: currentItem.description,
        quantity: qty,
        unit_id: currentItem.unit_id,
        unit: unit?.symbol || unit?.name || '',
        unit_name: unit?.name || '',
        rate: rate,
        amount: qty * rate
      };
    } else {
      // Goods item - lookup item and unit
      const item = items.find(i => i.id === currentItem.item_id);
      const unit = units.find(u => u.id === currentItem.unit_id);
      newItem = {
        item_id: currentItem.item_id,
        item_name: item?.name || '',
        quantity: qty,
        unit_id: currentItem.unit_id || item?.default_unit_id || '',
        unit_name: unit?.symbol || unit?.name || '',
        rate: rate,
        amount: qty * rate
      };
    }

    setFormData(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));

    setCurrentItem({ item_id: '', description: '', quantity: '', unit_id: '', unit: '', rate: '' });
  };

  // Handle opening the Create New Item dialog
  const handleOpenCreateItemDialog = () => {
    setNewItemData({
      name: '',
      code: '',
      category_id: '',
      default_unit_id: '',
      purchase_price: '',
      description: ''
    });
    setCreateItemDialogOpen(true);
  };

  // Handle saving a new item
  const handleSaveNewItem = async () => {
    if (!newItemData.name) {
      toast.error('Item name is required');
      return;
    }

    try {
      setSavingItem(true);
      const response = await itemAPI.create({
        name: newItemData.name,
        code: newItemData.code || undefined,
        category_id: newItemData.category_id || undefined,
        default_unit_id: newItemData.default_unit_id || undefined,
        purchase_price: newItemData.purchase_price ? parseFloat(newItemData.purchase_price) : 0,
        description: newItemData.description || undefined,
        status: 'active'
      });
      
      // Add the new item to the items list
      const newItem = response.data;
      setItems(prev => [...prev, newItem]);
      
      // Auto-select the new item in the current item form
      setCurrentItem(prev => ({
        ...prev,
        item_id: newItem.id,
        unit_id: newItem.default_unit_id || '',
        rate: newItem.purchase_price?.toString() || ''
      }));
      
      toast.success(`Item "${newItem.name}" created and selected`);
      setCreateItemDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create item');
    } finally {
      setSavingItem(false);
    }
  };

  const handleRemoveItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  // Handle inline editing of item quantity and rate
  const handleUpdateItem = (index, field, value) => {
    setFormData(prev => {
      const newItems = [...prev.items];
      const item = { ...newItems[index] };
      
      if (field === 'quantity') {
        item.quantity = parseFloat(value) || 0;
      } else if (field === 'rate') {
        item.rate = parseFloat(value) || 0;
      }
      
      // Recalculate amount
      item.amount = (item.quantity || 0) * (item.rate || 0);
      newItems[index] = item;
      
      return { ...prev, items: newItems };
    });
  };

  const handleSubmit = async () => {
    if (!formData.property_id || !formData.store_id) {
      toast.error('Please select property and store');
      return;
    }

    if (!useCustomVendor && !formData.vendor_id) {
      toast.error('Please select a vendor or choose "Other" to enter a custom vendor');
      return;
    }

    if (useCustomVendor && !formData.custom_vendor_name.trim()) {
      toast.error('Please enter the vendor name');
      return;
    }

    if (formData.items.length === 0) {
      toast.error('Please add at least one item');
      return;
    }

    try {
      setSaving(true);
      const submitData = {
        ...formData,
        vendor_id: useCustomVendor ? null : formData.vendor_id,
        custom_vendor_name: useCustomVendor ? formData.custom_vendor_name.trim() : null,
        client_id: formData.client_ids?.[0] || null,
        client_ids: formData.client_ids || [],
        committed_payment_date: formData.committed_payment_date || null
      };
      
      if (selectedOrder) {
        // Include items in update for draft/approved POs
        await purchaseOrderAPI.update(selectedOrder.id, {
          expected_delivery_date: formData.expected_delivery_date,
          shipping_address: formData.shipping_address,
          billing_address: formData.billing_address,
          terms_conditions: formData.terms_conditions,
          notes: formData.notes,
          items: formData.items,  // Include items for add/remove functionality
          client_id: formData.client_ids?.[0] || null,
          client_ids: formData.client_ids || [],
          committed_payment_date: formData.committed_payment_date || null,
          adjustment: parseFloat(formData.adjustment) || 0,
          adjustment_notes: formData.adjustment_notes || null
        });
        toast.success('Purchase order updated');
      } else {
        await purchaseOrderAPI.create(submitData);
        toast.success('Purchase order created');
      }
      handleCloseDialog();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save purchase order');
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async (order) => {
    try {
      await purchaseOrderAPI.approve(order.id);
      toast.success('Purchase order approved');
      fetchData();
    } catch (error) {
      toast.error('Failed to approve');
    }
  };

  const handleCancel = async (order) => {
    try {
      await purchaseOrderAPI.cancel(order.id);
      toast.success('Purchase order cancelled');
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel');
    }
  };

  const handleDelete = async () => {
    try {
      await purchaseOrderAPI.delete(selectedOrder.id);
      toast.success('Purchase order deleted');
      setDeleteDialogOpen(false);
      setSelectedOrder(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleDownloadPDF = async (order) => {
    try {
      setDownloading(order.id);
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/purchase-orders/${order.id}/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to download PDF');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${order.po_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    } finally {
      setDownloading(null);
    }
  };

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setViewDialogOpen(true);
  };

  const filteredOrders = orders.filter(order => {
    const matchesStatus = filterStatus === 'all' || order.status === filterStatus;
    const matchesPaymentStatus = filterPaymentStatus === 'all' || order.payment_status === filterPaymentStatus;
    const matchesClient = filterClient === 'all' || 
      (filterClient === 'unassigned' ? !order.client_id : order.client_id === filterClient);
    const matchesCreatedBy = filterCreatedBy === 'all' || order.created_by === filterCreatedBy;
    const matchesVendor = filterVendor === 'all' || order.vendor_id === filterVendor;
    const hasGRN = (order.grns || []).length > 0;
    const matchesGRN = filterGRN === 'all' || (filterGRN === 'received' ? hasGRN : !hasGRN);
    return matchesStatus && matchesPaymentStatus && matchesClient && matchesCreatedBy && matchesVendor && matchesGRN;
  });

  const calculateTotal = () => {
    const itemsTotal = formData.items.reduce((sum, item) => sum + (item.amount || 0), 0);
    const adjustment = parseFloat(formData.adjustment) || 0;
    return itemsTotal + adjustment;
  };

  const calculateItemsSubtotal = () => {
    return formData.items.reduce((sum, item) => sum + (item.amount || 0), 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Purchase Orders</h1>
          <p className="text-muted-foreground mt-1">Create and manage purchase orders</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button 
            variant="outline" 
            onClick={handleRecalculateAll}
            disabled={recalculatingAll || !isSuperAdmin}
            data-testid="recalculate-all-btn"
            className="border-orange-300 text-orange-700 hover:bg-orange-50"
            title={!isSuperAdmin ? "Only Super Admin can recalculate all POs" : "Recalculate balance for all POs"}
          >
            {recalculatingAll ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Recalculate All POs
          </Button>
          <Button onClick={() => handleOpenDialog()} data-testid="create-po-btn">
            <Plus className="h-4 w-4 mr-2" />
            Create Purchase Order
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[150px]" data-testid="po-status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterPaymentStatus} onValueChange={setFilterPaymentStatus}>
              <SelectTrigger className="w-[150px]" data-testid="po-payment-filter">
                <SelectValue placeholder="Payment" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Payments</SelectItem>
                <SelectItem value="unpaid">Unpaid</SelectItem>
                <SelectItem value="partial">Partial</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterClient} onValueChange={setFilterClient}>
              <SelectTrigger className="w-[180px]" data-testid="po-client-filter">
                <SelectValue placeholder="Client" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Clients</SelectItem>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {clients.map(client => (
                  <SelectItem key={client.id} value={client.id}>{client.client_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterVendor} onValueChange={setFilterVendor}>
              <SelectTrigger className="w-[180px]" data-testid="po-vendor-filter">
                <SelectValue placeholder="Vendor" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Vendors</SelectItem>
                {vendors.map(vendor => (
                  <SelectItem key={vendor.id} value={vendor.id}>{vendor.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterCreatedBy} onValueChange={setFilterCreatedBy}>
              <SelectTrigger className="w-[180px]" data-testid="po-created-by-filter">
                <SelectValue placeholder="Created By" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Users</SelectItem>
                {users.map(user => (
                  <SelectItem key={user.id} value={user.id}>{user.full_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterGRN} onValueChange={setFilterGRN}>
              <SelectTrigger className="w-[160px]" data-testid="po-grn-filter">
                <SelectValue placeholder="GRN" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All GRN</SelectItem>
                <SelectItem value="received">GRN Received</SelectItem>
                <SelectItem value="not_received">Not Received</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Actions Bar */}
      {selectedPOs.length > 0 && (
        <Card className="border-primary">
          <CardContent className="py-3 px-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Badge variant="secondary">{selectedPOs.length} selected</Badge>
                <span className="text-sm text-muted-foreground">Purchase Orders</span>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setSelectedPOs([])}
                >
                  Clear Selection
                </Button>
                <Button 
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadSummaryPDF}
                  disabled={downloading === 'summary'}
                  data-testid="download-summary-btn"
                >
                  {downloading === 'summary' ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4 mr-2" />
                  )}
                  Download Summary
                </Button>
                <Button 
                  variant="default"
                  size="sm"
                  onClick={handleBulkApprove}
                  disabled={saving || orders.filter(o => selectedPOs.includes(o.id) && o.status === 'draft').length === 0}
                  data-testid="bulk-approve-btn"
                  className="bg-green-600 hover:bg-green-700"
                >
                  {saving ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="h-4 w-4 mr-2" />
                  )}
                  Approve Selected ({orders.filter(o => selectedPOs.includes(o.id) && o.status === 'draft').length})
                </Button>
                <Button 
                  size="sm"
                  onClick={() => setBulkAssignDialogOpen(true)}
                  data-testid="bulk-assign-client-btn"
                >
                  <UserCircle className="h-4 w-4 mr-2" />
                  Assign to Client
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Orders Table */}
      <Card>
        <CardContent className="p-6">
          <DataTable
            module="purchase_orders"
            data={filteredOrders}
            columns={[
              {
                key: 'select',
                label: (
                  <Checkbox
                    checked={selectedPOs.length === filteredOrders.length && filteredOrders.length > 0}
                    onCheckedChange={handleSelectAll}
                    data-testid="select-all-pos"
                  />
                ),
                sortable: false,
                width: '40px',
                render: (_, order) => (
                  <Checkbox
                    checked={selectedPOs.includes(order.id)}
                    onCheckedChange={(checked) => handleSelectPO(order.id, checked)}
                    data-testid={`select-po-${order.id}`}
                  />
                )
              },
              {
                key: 'po_number',
                label: 'PO Number',
                sortable: true,
                render: (value) => <span className="font-medium font-mono">{value}</span>
              },
              {
                key: 'po_type',
                label: 'Type',
                sortable: true,
                render: (value) => (
                  <Badge variant={value === 'service' ? 'secondary' : 'default'} className="text-xs">
                    {value === 'service' ? 'Service' : 'Goods'}
                  </Badge>
                )
              },
              {
                key: 'date',
                label: 'PO Date',
                sortable: true,
                render: (value) => formatDate(value)
              },
              {
                key: 'bill_date',
                label: 'Bill Date',
                sortable: true,
                render: (value) => value ? formatDate(value) : <span className="text-muted-foreground text-xs">-</span>
              },
              {
                key: 'bill_number',
                label: 'Bill #',
                sortable: true,
                render: (value) => value ? <span className="font-mono text-sm">{value}</span> : <span className="text-muted-foreground text-xs">-</span>
              },
              {
                key: 'grns',
                label: 'GRN',
                sortable: false,
                render: (_, order) => {
                  const grns = order.grns || [];
                  if (grns.length === 0) {
                    return <Badge variant="outline" className="text-xs text-muted-foreground" data-testid={`po-grn-none-${order.id}`}>Not received</Badge>;
                  }
                  return (
                    <div className="flex flex-wrap gap-1" data-testid={`po-grn-list-${order.id}`}>
                      {grns.map((g) => (
                        <button
                          key={g.id}
                          onClick={(e) => { e.stopPropagation(); navigate(`/grn?view=${g.id}`); }}
                          className="inline-flex items-center gap-1 rounded-md border border-green-200 bg-green-50 px-1.5 py-0.5 text-xs font-mono text-green-700 hover:bg-green-100 transition-colors"
                          title="View GRN"
                          data-testid={`po-grn-link-${g.id}`}
                        >
                          <ClipboardCheck className="h-3 w-3" />
                          {g.grn_number}
                        </button>
                      ))}
                    </div>
                  );
                }
              },
              {
                key: 'vendor_name',
                label: 'Vendor',
                sortable: true,
                render: (_, order) => order.vendor_name || order.custom_vendor_name || '-'
              },
              {
                key: 'items_count',
                label: 'Items',
                sortable: true,
                render: (_, order) => (
                  <Badge variant="secondary" className="font-mono">
                    {order.items?.length || 0}
                  </Badge>
                )
              },
              {
                key: 'client_name',
                label: 'Client',
                sortable: true,
                render: (value, order) => {
                  const names = (order.client_names && order.client_names.length > 0)
                    ? order.client_names
                    : (order.client_name ? [order.client_name] : []);
                  if (names.length === 0) return <span className="text-muted-foreground text-xs">-</span>;
                  return (
                    <Badge variant="outline" className="gap-1" title={names.join(', ')}>
                      <UserCircle className="h-3 w-3" />
                      {names[0]}{names.length > 1 ? ` +${names.length - 1}` : ''}
                    </Badge>
                  );
                }
              },
              {
                key: 'created_by_name',
                label: 'Created By',
                sortable: true,
                render: (value) => (
                  value ? (
                    <span className="text-sm">{value}</span>
                  ) : (
                    <span className="text-muted-foreground text-xs">-</span>
                  )
                )
              },
              {
                key: 'total_amount',
                label: 'Total',
                sortable: true,
                render: (_, order) => (
                  <span className="font-semibold">
                    {formatCurrency(order.actual_amount || order.total_amount)}
                  </span>
                )
              },
              {
                key: 'payment_status',
                label: 'Payment',
                sortable: true,
                render: (_, order) => (
                  <div className="flex flex-col gap-1">
                    <Badge className={`${getPaymentStatusColor(order.payment_status)} text-xs`}>
                      {order.payment_status || 'unpaid'}
                    </Badge>
                    {order.balance_due > 0 && (
                      <span className="text-xs text-muted-foreground">
                        Due: {formatCurrency(order.balance_due)}
                      </span>
                    )}
                  </div>
                )
              },
              {
                key: 'payment_screenshot',
                label: 'Receipt',
                sortable: false,
                render: (_, order) => {
                  const shots = (order.payment_screenshots?.length > 0)
                    ? order.payment_screenshots
                    : (order.payment_screenshot ? [order.payment_screenshot] : []);
                  return shots.length > 0 ? (
                    <div className="flex items-center gap-0.5">
                      {shots.map((shot, idx) => (
                        <Button
                          key={idx}
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => viewBill(shot)}
                          title={`View Payment Screenshot ${idx + 1}`}
                        >
                          <Eye className="h-3.5 w-3.5 text-blue-600" />
                        </Button>
                      ))}
                      {shots.length > 1 && <span className="text-xs text-muted-foreground">({shots.length})</span>}
                    </div>
                  ) : (
                    <span className="text-muted-foreground text-xs">-</span>
                  );
                }
              },
              {
                key: 'status',
                label: 'Status',
                sortable: true,
                render: (value) => (
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(value)}`}>
                    {value}
                  </span>
                )
              }
            ]}
            renderActions={(order) => (
              <div className="flex items-center justify-end gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleViewOrder(order)}
                  title="View"
                >
                  <Eye className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleOpenPaymentDialog(order)}
                  title="Update Payment"
                  className="text-green-600"
                  data-testid={`payment-btn-${order.id}`}
                >
                  <IndianRupee className="h-4 w-4" />
                </Button>
                {isSuperAdmin && order.actual_amount > 0 && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRecalculateBalance(order)}
                    disabled={recalculating === order.id}
                    title="Recalculate Balance (based on GRN)"
                    className="text-orange-600"
                    data-testid={`recalculate-btn-${order.id}`}
                  >
                    {recalculating === order.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4" />
                    )}
                  </Button>
                )}
                {isSuperAdmin && order.actual_amount > 0 && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRepairFromGRN(order)}
                    disabled={recalculating === order.id}
                    title="Repair PO Items & Amounts from GRN"
                    className="text-purple-600"
                    data-testid={`repair-btn-${order.id}`}
                  >
                    {recalculating === order.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Wrench className="h-4 w-4" />
                    )}
                  </Button>
                )}
                {['draft', 'approved', 'open'].includes(order.status) && order.po_type !== 'service' && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => navigate(`/grn/create/${order.id}`)}
                    title="Receive Goods (GRN)"
                    className="text-blue-600"
                    data-testid={`receive-items-${order.id}`}
                  >
                    <Truck className="h-4 w-4" />
                  </Button>
                )}
                {['approved', 'open'].includes(order.status) && order.po_type === 'service' && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => navigate(`/srn?po_id=${order.id}`)}
                    title="Receive Service (SRN)"
                    className="text-emerald-600"
                    data-testid={`receive-service-${order.id}`}
                  >
                    <ClipboardCheck className="h-4 w-4" />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDownloadPDF(order)}
                  disabled={downloading === order.id}
                  title="Download PDF"
                >
                  {downloading === order.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                </Button>
                {order.status === 'draft' && (
                  <>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleApprove(order)}
                      title="Approve"
                      className="text-green-600"
                    >
                      <CheckCircle className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleOpenDialog(order)}
                      title="Edit"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setSelectedOrder(order);
                        setDeleteDialogOpen(true);
                      }}
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </>
                )}
                {/* Super Admin can edit any PO */}
                {isSuperAdmin && order.status !== 'draft' && order.status !== 'cancelled' && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleOpenDialog(order)}
                    title="Edit PO (Super Admin)"
                    className="text-purple-600"
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                )}
                {order.status === 'approved' && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleCancel(order)}
                    title="Cancel"
                    className="text-red-600"
                  >
                    <XCircle className="h-4 w-4" />
                  </Button>
                )}
                {/* Super Admin can cancel any non-cancelled PO */}
                {isSuperAdmin && order.status !== 'draft' && order.status !== 'approved' && order.status !== 'cancelled' && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleCancel(order)}
                    title="Cancel PO (Super Admin)"
                    className="text-red-600"
                  >
                    <XCircle className="h-4 w-4" />
                  </Button>
                )}
                {/* Super Admin can delete any PO */}
                {isSuperAdmin && order.status !== 'draft' && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setSelectedOrder(order);
                      setDeleteDialogOpen(true);
                    }}
                    title="Delete PO (Super Admin)"
                    className="text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            )}
            title="Purchase Orders"
            loading={loading}
          />
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="!w-[90vw] !max-w-[1100px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">{selectedOrder ? 'Edit Purchase Order' : 'Create Purchase Order'}</DialogTitle>
            <DialogDescription>
              {selectedOrder ? 'Update purchase order details' : 'Fill in the details to create a new purchase order'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* PO Type Selection */}
            <div className="flex items-center gap-4 p-4 bg-muted rounded-lg">
              <Label className="font-medium">Purchase Order Type:</Label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="po_type"
                    value="goods"
                    checked={formData.po_type === 'goods'}
                    onChange={(e) => setFormData({ ...formData, po_type: e.target.value, items: [] })}
                    disabled={!!selectedOrder}
                    className="h-4 w-4"
                  />
                  <span className={formData.po_type === 'goods' ? 'font-medium' : ''}>
                    Goods (Stock Items)
                  </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="po_type"
                    value="service"
                    checked={formData.po_type === 'service'}
                    onChange={(e) => setFormData({ ...formData, po_type: e.target.value, items: [] })}
                    disabled={!!selectedOrder}
                    className="h-4 w-4"
                  />
                  <span className={formData.po_type === 'service' ? 'font-medium' : ''}>
                    Service (No Stock)
                  </span>
                </label>
              </div>
              {formData.po_type === 'service' && (
                <Badge variant="secondary" className="ml-auto">Use SRN to record service received</Badge>
              )}
            </div>

            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Property *</Label>
                <Select
                  value={formData.property_id}
                  onValueChange={(value) => setFormData({ ...formData, property_id: value, store_id: '' })}
                  disabled={!!selectedOrder}
                >
                  <SelectTrigger data-testid="po-property-select">
                    <SelectValue placeholder="Select property" />
                  </SelectTrigger>
                  <SelectContent>
                    {properties.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Store *</Label>
                <Select
                  value={formData.store_id}
                  onValueChange={(value) => setFormData({ ...formData, store_id: value })}
                  disabled={!formData.property_id || !!selectedOrder}
                >
                  <SelectTrigger data-testid="po-store-select">
                    <SelectValue placeholder="Select store" />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredStores.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Vendor *</Label>
                <Select
                  value={useCustomVendor ? '__custom__' : formData.vendor_id}
                  onValueChange={(value) => {
                    if (value === '__custom__') {
                      setUseCustomVendor(true);
                      setFormData({ ...formData, vendor_id: '' });
                    } else {
                      setUseCustomVendor(false);
                      setFormData({ ...formData, vendor_id: value, custom_vendor_name: '' });
                    }
                  }}
                  disabled={!!selectedOrder}
                >
                  <SelectTrigger data-testid="po-vendor-select">
                    <SelectValue placeholder="Select vendor" />
                  </SelectTrigger>
                  <SelectContent>
                    {vendors.map(v => (
                      <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                    ))}
                    <SelectItem value="__custom__" className="text-primary font-medium">
                      Other / Non-listed Vendor
                    </SelectItem>
                  </SelectContent>
                </Select>
                
                {useCustomVendor && (
                  <div className="mt-2">
                    <Input
                      placeholder="Enter vendor name"
                      value={formData.custom_vendor_name}
                      onChange={(e) => setFormData({ ...formData, custom_vendor_name: e.target.value })}
                      data-testid="po-custom-vendor-input"
                      className="border-primary"
                      autoFocus
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Expected Delivery Date</Label>
                <Input
                  type="date"
                  value={formData.expected_delivery_date}
                  onChange={(e) => setFormData({ ...formData, expected_delivery_date: e.target.value })}
                  data-testid="po-delivery-date"
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <UserCircle className="h-4 w-4" />
                  Assign to Client(s)
                </Label>
                <Select
                  value=""
                  onValueChange={(value) => {
                    if (value && !formData.client_ids.includes(value)) {
                      setFormData({ ...formData, client_ids: [...formData.client_ids, value] });
                    }
                  }}
                >
                  <SelectTrigger data-testid="po-client-select">
                    <SelectValue placeholder="Add client(s) (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.filter(c => !formData.client_ids.includes(c.id)).map(client => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.client_name} {client.event_type ? `(${client.event_type})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formData.client_ids.length > 0 && (
                  <div className="flex flex-wrap gap-2 pt-1" data-testid="po-selected-clients">
                    {formData.client_ids.map(cid => {
                      const c = clients.find(cl => cl.id === cid);
                      return (
                        <Badge key={cid} variant="secondary" className="flex items-center gap-1 pr-1" data-testid={`po-client-chip-${cid}`}>
                          {c?.client_name || cid}
                          <button
                            type="button"
                            className="ml-1 rounded-full hover:bg-muted-foreground/20 p-0.5"
                            onClick={() => setFormData({ ...formData, client_ids: formData.client_ids.filter(id => id !== cid) })}
                            data-testid={`po-client-chip-remove-${cid}`}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      );
                    })}
                  </div>
                )}
                {formData.client_ids.length > 1 && (
                  <p className="text-xs text-amber-600">Multiple clients selected — you'll split the amount per client when creating the GRN.</p>
                )}
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Payment Due Date
                </Label>
                <Input
                  type="date"
                  value={formData.committed_payment_date}
                  onChange={(e) => setFormData({ ...formData, committed_payment_date: e.target.value })}
                  data-testid="po-payment-date"
                />
              </div>
            </div>

            {/* Services Section - Only for Service POs */}
            {formData.po_type === 'service' && (!poHasGrn && (!selectedOrder || selectedOrder.status === 'draft' || selectedOrder.status === 'approved' || (isSuperAdmin && selectedOrder.status !== 'cancelled'))) && (
              <div className="space-y-4 p-4 md:p-6 border-2 border-blue-200 rounded-lg bg-blue-50/30">
                <div className="flex items-center gap-2">
                  <Briefcase className="h-5 w-5 text-blue-600" />
                  <Label className="text-base font-semibold text-blue-800">
                    {selectedOrder ? 'Add/Edit Services' : 'Add Services'}
                  </Label>
                </div>
                <p className="text-sm text-muted-foreground">
                  Define the services to be provided. Services do not affect inventory stock.
                </p>
                <div className="grid grid-cols-12 gap-3 items-end">
                  <div className="col-span-12 md:col-span-5">
                    <Label className="text-xs text-muted-foreground mb-1">Service Description *</Label>
                    <Input
                      placeholder="e.g., HVAC Maintenance, Pest Control, Plumbing Repair"
                      value={currentItem.description}
                      onChange={(e) => setCurrentItem({ ...currentItem, description: e.target.value })}
                      data-testid="po-service-description"
                    />
                  </div>
                  <div className="col-span-4 md:col-span-2">
                    <Label className="text-xs text-muted-foreground mb-1">Quantity *</Label>
                    <Input
                      type="number"
                      placeholder="1"
                      min="0.01"
                      step="0.01"
                      value={currentItem.quantity}
                      onChange={(e) => setCurrentItem({ ...currentItem, quantity: e.target.value })}
                      data-testid="po-service-qty"
                    />
                  </div>
                  <div className="col-span-8 md:col-span-2">
                    <Label className="text-xs text-muted-foreground mb-1">Unit *</Label>
                    <Select
                      value={currentItem.unit_id}
                      onValueChange={(value) => {
                        const unit = units.find(u => u.id === value);
                        setCurrentItem({ 
                          ...currentItem, 
                          unit_id: value,
                          unit: unit?.symbol || unit?.name || ''
                        });
                      }}
                    >
                      <SelectTrigger data-testid="po-service-unit-select">
                        <SelectValue placeholder="Select unit" />
                      </SelectTrigger>
                      <SelectContent>
                        {units.map(unit => (
                          <SelectItem key={unit.id} value={unit.id}>
                            {unit.name} {unit.symbol ? `(${unit.symbol})` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-6 md:col-span-2">
                    <Label className="text-xs text-muted-foreground mb-1">Rate (₹) *</Label>
                    <Input
                      type="number"
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                      value={currentItem.rate}
                      onChange={(e) => setCurrentItem({ ...currentItem, rate: e.target.value })}
                      data-testid="po-service-rate"
                    />
                  </div>
                  <div className="col-span-6 md:col-span-1">
                    <Button onClick={handleAddItem} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="po-add-service-btn">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Items Section - Only for Goods POs */}
            {formData.po_type === 'goods' && (!poHasGrn && (!selectedOrder || selectedOrder.status === 'draft' || selectedOrder.status === 'approved' || (isSuperAdmin && selectedOrder.status !== 'cancelled'))) && (
              <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
                <Label className="text-base font-semibold">
                  {selectedOrder ? 'Add/Edit Items' : 'Add Items'}
                </Label>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                  <div className="md:col-span-2">
                    <SearchableItemSelect
                      items={items}
                      value={currentItem.item_id}
                      onSelect={(value) => {
                        const item = items.find(i => i.id === value);
                        setCurrentItem({ 
                          ...currentItem, 
                          item_id: value,
                          unit_id: item?.default_unit_id || '',
                          rate: item?.purchase_price?.toString() || ''
                        });
                      }}
                      placeholder="Search item..."
                      testId="po-item-select"
                      showCreateNew={true}
                      onCreateNew={handleOpenCreateItemDialog}
                    />
                  </div>
                  <div>
                    <Input
                      type="number"
                      placeholder="Qty"
                      value={currentItem.quantity}
                      onChange={(e) => setCurrentItem({ ...currentItem, quantity: e.target.value })}
                      data-testid="po-item-qty"
                    />
                  </div>
                  <div>
                    <Input
                      type="number"
                      placeholder="Rate"
                      value={currentItem.rate}
                      onChange={(e) => setCurrentItem({ ...currentItem, rate: e.target.value })}
                      data-testid="po-item-rate"
                    />
                  </div>
                  <div>
                    <Button onClick={handleAddItem} className="w-full" data-testid="po-add-item-btn">
                      <Plus className="h-4 w-4 mr-1" /> Add
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* GRN lock notice */}
            {poHasGrn && selectedOrder && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2" data-testid="po-grn-lock-notice">
                <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                <div className="text-sm text-amber-800">
                  Goods have been received against this PO ({poGrnCount} GRN{poGrnCount > 1 ? 's' : ''}). Item quantities and amounts are now driven by the GRN — to change them, edit the GRN and the PO total will update automatically.
                  <Button variant="link" className="h-auto p-0 ml-1 text-amber-900 underline" onClick={() => { setDialogOpen(false); navigate('/grn'); }} data-testid="po-go-to-grn-btn">Go to GRNs</Button>
                </div>
              </div>
            )}

            {/* Items Table */}
            {formData.items.length > 0 && (
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{formData.po_type === 'service' ? 'Service' : 'Item'}</TableHead>
                      <TableHead className="w-[120px]">Qty</TableHead>
                      <TableHead className="w-[120px]">Rate</TableHead>
                      <TableHead>Amount</TableHead>
                      {(!poHasGrn && (!selectedOrder || selectedOrder.status === 'draft' || selectedOrder.status === 'approved' || (isSuperAdmin && selectedOrder.status !== 'cancelled'))) && <TableHead className="w-[50px]"></TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {formData.items.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.description || item.item_name}</TableCell>
                        <TableCell>
                          {isSuperAdmin && selectedOrder && selectedOrder.status !== 'cancelled' && !poHasGrn ? (
                            <Input
                              type="number"
                              value={item.quantity}
                              onChange={(e) => handleUpdateItem(index, 'quantity', e.target.value)}
                              className="w-[100px] h-8"
                              min="0"
                              step="0.01"
                            />
                          ) : (
                            <span>{item.quantity} {item.unit || item.unit_name}</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {isSuperAdmin && selectedOrder && selectedOrder.status !== 'cancelled' && !poHasGrn ? (
                            <Input
                              type="number"
                              value={item.rate}
                              onChange={(e) => handleUpdateItem(index, 'rate', e.target.value)}
                              className="w-[100px] h-8"
                              min="0"
                              step="0.01"
                            />
                          ) : (
                            formatCurrency(item.rate)
                          )}
                        </TableCell>
                        <TableCell className="font-medium">{formatCurrency(item.amount)}</TableCell>
                        {(!poHasGrn && (!selectedOrder || selectedOrder.status === 'draft' || selectedOrder.status === 'approved' || (isSuperAdmin && selectedOrder.status !== 'cancelled'))) && (
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleRemoveItem(index)}
                              title="Remove item"
                            >
                              <X className="h-4 w-4 text-destructive" />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                    <TableRow className="bg-muted/30">
                      <TableCell colSpan={3} className="text-right font-semibold">Subtotal:</TableCell>
                      <TableCell className="font-semibold">{formatCurrency(calculateItemsSubtotal())}</TableCell>
                      {(!poHasGrn && (!selectedOrder || selectedOrder.status === 'draft' || selectedOrder.status === 'approved' || (isSuperAdmin && selectedOrder.status !== 'cancelled'))) && <TableCell></TableCell>}
                    </TableRow>
                    {/* Adjustment Row - Super Admin Only */}
                    {isSuperAdmin && selectedOrder && selectedOrder.status !== 'cancelled' && !poHasGrn && (
                      <TableRow className="bg-amber-50/50">
                        <TableCell colSpan={2} className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Label className="text-amber-700 font-medium">Adjustment:</Label>
                            <Input
                              placeholder="Notes (e.g., discount, extra charges)"
                              value={formData.adjustment_notes}
                              onChange={(e) => setFormData({ ...formData, adjustment_notes: e.target.value })}
                              className="w-[200px] h-8 text-sm"
                            />
                          </div>
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            value={formData.adjustment}
                            onChange={(e) => setFormData({ ...formData, adjustment: e.target.value })}
                            className="w-[100px] h-8"
                            placeholder="+/-"
                            step="0.01"
                          />
                        </TableCell>
                        <TableCell className={`font-medium ${parseFloat(formData.adjustment) < 0 ? 'text-red-600' : parseFloat(formData.adjustment) > 0 ? 'text-green-600' : ''}`}>
                          {parseFloat(formData.adjustment) !== 0 ? formatCurrency(parseFloat(formData.adjustment) || 0) : '-'}
                        </TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    )}
                    <TableRow className="border-t-2 border-primary">
                      <TableCell colSpan={3} className="text-right font-bold text-lg">Grand Total:</TableCell>
                      <TableCell className="font-bold text-lg text-primary">{formatCurrency(calculateTotal())}</TableCell>
                      {(!poHasGrn && (!selectedOrder || selectedOrder.status === 'draft' || selectedOrder.status === 'approved' || (isSuperAdmin && selectedOrder.status !== 'cancelled'))) && <TableCell></TableCell>}
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Additional Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Shipping Address</Label>
                <Textarea
                  value={formData.shipping_address}
                  onChange={(e) => setFormData({ ...formData, shipping_address: e.target.value })}
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label>Billing Address</Label>
                <Textarea
                  value={formData.billing_address}
                  onChange={(e) => setFormData({ ...formData, billing_address: e.target.value })}
                  rows={3}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Terms & Conditions</Label>
              <Textarea
                value={formData.terms_conditions}
                onChange={(e) => setFormData({ ...formData, terms_conditions: e.target.value })}
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={saving} data-testid="po-save-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {selectedOrder ? 'Update' : 'Create'} Purchase Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="!w-[90vw] !max-w-[1100px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <FileText className="h-5 w-5" />
              {selectedOrder?.po_number}
            </DialogTitle>
          </DialogHeader>

          {selectedOrder && (
            <div className="space-y-6 py-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Date:</span>
                  <span className="ml-2 font-medium">{formatDate(selectedOrder.date)}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Status:</span>
                  <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedOrder.status)}`}>
                    {selectedOrder.status}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Property:</span>
                  <span className="ml-2 font-medium">{selectedOrder.property_name}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Store:</span>
                  <span className="ml-2 font-medium">{selectedOrder.store_name}</span>
                </div>
                <div className="col-span-2">
                  <span className="text-muted-foreground">Vendor:</span>
                  <span className="ml-2 font-medium">
                    {selectedOrder.vendor_name || selectedOrder.custom_vendor_name}
                    {selectedOrder.custom_vendor_name && !selectedOrder.vendor_name && (
                      <span className="ml-2 text-xs text-muted-foreground">(Custom)</span>
                    )}
                  </span>
                </div>
              </div>

              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead>Qty</TableHead>
                      <TableHead>Rate</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedOrder.items?.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.item_name || item.description || '-'}</TableCell>
                        <TableCell>{item.quantity} {item.unit_name || item.unit}</TableCell>
                        <TableCell>{formatCurrency(item.rate)}</TableCell>
                        <TableCell className="text-right font-medium">{formatCurrency(item.amount)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={3} className="text-right font-semibold">Total:</TableCell>
                      <TableCell className="text-right font-bold text-lg">{formatCurrency(selectedOrder.total_amount)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>

              {selectedOrder.notes && (
                <div>
                  <Label className="text-muted-foreground">Notes:</Label>
                  <p className="mt-1">{selectedOrder.notes}</p>
                </div>
              )}

              {/* Payment Information */}
              {(selectedOrder.payment_made > 0 || selectedOrder.payment_screenshot) && (
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <Label className="text-green-700 font-medium">Payment Information</Label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-2 text-sm">
                    <div>
                      <span className="text-muted-foreground">Amount Paid:</span>
                      <span className="ml-2 font-medium">{formatCurrency(selectedOrder.payment_made || 0)}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Balance Due:</span>
                      <span className="ml-2 font-medium">{formatCurrency(selectedOrder.balance_due || 0)}</span>
                    </div>
                    {selectedOrder.payment_mode && (
                      <div>
                        <span className="text-muted-foreground">Mode:</span>
                        <span className="ml-2 font-medium">{selectedOrder.payment_mode}</span>
                      </div>
                    )}
                    {selectedOrder.payment_date && (
                      <div>
                        <span className="text-muted-foreground">Date:</span>
                        <span className="ml-2 font-medium">{formatDate(selectedOrder.payment_date)}</span>
                      </div>
                    )}
                  </div>
                  {(() => {
                    const shots = (selectedOrder.payment_screenshots?.length > 0)
                      ? selectedOrder.payment_screenshots
                      : (selectedOrder.payment_screenshot ? [selectedOrder.payment_screenshot] : []);
                    return shots.length > 0 ? (
                      <div className="mt-3 flex items-start gap-2 flex-wrap">
                        <span className="text-sm text-muted-foreground">Payment Screenshot{shots.length > 1 ? 's' : ''}:</span>
                        {shots.map((shot, idx) => (
                          <div key={idx} className="flex items-center gap-1" data-testid={`po-payment-screenshot-${idx}`}>
                            <Button size="sm" variant="outline" onClick={() => viewBill(shot)} className="h-7">
                              <Eye className="h-3 w-3 mr-1" /> View{shots.length > 1 ? ` ${idx + 1}` : ''}
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => downloadBill(shot, `payment_${selectedOrder.po_number}_${idx + 1}`)} className="h-7 text-green-600">
                              <Download className="h-3 w-3 mr-1" /> Download
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : null;
                  })()}
                  {selectedOrder.payment_notes && (
                    <div className="mt-2">
                      <span className="text-sm text-muted-foreground">Notes:</span>
                      <p className="text-sm mt-1">{selectedOrder.payment_notes}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)}>Close</Button>
            <Button onClick={() => selectedOrder && handleDownloadPDF(selectedOrder)}>
              <Download className="h-4 w-4 mr-2" />
              Download PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-destructive">Delete Purchase Order</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{selectedOrder?.po_number}</strong>? This action cannot be undone.
              {selectedOrder?.actual_amount > 0 && (
                <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800">
                  <strong>Warning:</strong> This PO has received goods (GRNs). Deleting will also remove all linked GRNs and revert stock changes.
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Update Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <IndianRupee className="h-5 w-5 text-green-600" />
              Record Payment - {selectedOrder?.po_number}
            </DialogTitle>
            <DialogDescription>
              Log each payment you make against this purchase order
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* PO Summary */}
            <div className="bg-muted/50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Total Amount:</span>
                <span className="font-semibold">{formatCurrency(selectedOrder?.actual_amount || selectedOrder?.total_amount)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Previously Paid:</span>
                <span className="font-medium text-green-600">{formatCurrency(selectedOrder?.payment_made || 0)}</span>
              </div>
              <div className="flex justify-between text-sm border-t pt-2">
                <span className="text-muted-foreground">Balance Due:</span>
                <span className="font-bold text-red-600">{formatCurrency(selectedOrder?.balance_due || (selectedOrder?.actual_amount || selectedOrder?.total_amount))}</span>
              </div>
            </div>

            {/* Payment Amount */}
            <div className="space-y-2">
              <Label htmlFor="payment_amount">New Payment Amount (₹) *</Label>
              <Input
                id="payment_amount"
                type="number"
                min="0"
                step="0.01"
                value={paymentFormData.payment_made}
                onChange={(e) => setPaymentFormData({ ...paymentFormData, payment_made: e.target.value })}
                placeholder="e.g. 2500"
                data-testid="payment-amount-input"
              />
              <p className="text-xs text-muted-foreground">
                Enter only this payment's amount. It will be added to the total paid.
              </p>
            </div>

            {/* Payment Made By & Mode - Two columns */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <UserCircle className="h-4 w-4" />
                  Payment Made By
                </Label>
                <Select
                  value={paymentFormData.payment_made_by}
                  onValueChange={(value) => setPaymentFormData({ ...paymentFormData, payment_made_by: value })}
                >
                  <SelectTrigger data-testid="payment-made-by-select">
                    <SelectValue placeholder="Select user" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map(user => (
                      <SelectItem key={user.id} value={user.id}>{user.full_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <CreditCard className="h-4 w-4" />
                  Mode of Payment
                </Label>
                <Select
                  value={paymentFormData.payment_mode}
                  onValueChange={(value) => setPaymentFormData({ ...paymentFormData, payment_mode: value })}
                >
                  <SelectTrigger data-testid="payment-mode-select">
                    <SelectValue placeholder="Select mode" />
                  </SelectTrigger>
                  <SelectContent>
                    {paymentModes.map(mode => (
                      <SelectItem key={mode} value={mode}>{mode}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Payment Date */}
            <div className="space-y-2">
              <Label htmlFor="actual_payment_date" className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Payment Date
              </Label>
              <Input
                id="actual_payment_date"
                type="date"
                value={paymentFormData.payment_date}
                onChange={(e) => setPaymentFormData({ ...paymentFormData, payment_date: e.target.value })}
                data-testid="actual-payment-date-input"
              />
            </div>

            {/* Payment Screenshot Upload (supports multiple) */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Upload className="h-4 w-4" />
                Payment Screenshots
              </Label>
              <Input
                type="file"
                ref={paymentScreenshotRef}
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv"
                multiple
                onChange={handlePaymentScreenshotUpload}
                className="hidden"
              />
              <Button 
                type="button" 
                variant="outline" 
                className="w-full"
                onClick={() => paymentScreenshotRef.current?.click()}
                disabled={uploadingScreenshot}
                data-testid="upload-payment-screenshot-btn"
              >
                {uploadingScreenshot ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Uploading...</>
                ) : (paymentFormData.payment_screenshots?.length > 0) ? (
                  <><Plus className="h-4 w-4 mr-2" />Add More ({paymentFormData.payment_screenshots.length} uploaded)</>
                ) : (
                  <><Upload className="h-4 w-4 mr-2" />Upload Screenshot(s)</>
                )}
              </Button>
              {(paymentFormData.payment_screenshots?.length > 0) && (
                <div className="space-y-1">
                  {paymentFormData.payment_screenshots.map((url, index) => (
                    <div key={index} className="flex items-center justify-between bg-muted/40 rounded-md px-2 py-1.5" data-testid={`payment-screenshot-item-${index}`}>
                      <span className="text-xs text-muted-foreground">Screenshot {index + 1}</span>
                      <div className="flex items-center gap-1">
                        <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => viewBill(url)} title="View"><Eye className="h-3.5 w-3.5" /></Button>
                        <Button type="button" variant="ghost" size="icon" className="h-6 w-6 text-green-600" onClick={() => downloadBill(url, `payment_${selectedOrder?.po_number}_${index + 1}`)} title="Download"><Download className="h-3.5 w-3.5" /></Button>
                        <Button type="button" variant="ghost" size="icon" className="h-6 w-6 text-red-600" onClick={() => removePaymentScreenshot(index)} title="Remove"><X className="h-3.5 w-3.5" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Payment Notes */}
            <div className="space-y-2">
              <Label htmlFor="payment_notes">Payment Notes</Label>
              <Textarea
                id="payment_notes"
                value={paymentFormData.payment_notes}
                onChange={(e) => setPaymentFormData({ ...paymentFormData, payment_notes: e.target.value })}
                placeholder="Add any notes about this payment..."
                rows={2}
                data-testid="payment-notes-input"
              />
            </div>

            {/* Payment History */}
            <div className="space-y-2 border-t pt-4">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2">
                  <CreditCard className="h-4 w-4" />
                  Payment History ({paymentHistory.length})
                </Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadPaymentReport}
                  disabled={downloadingReport || paymentHistory.length === 0}
                  data-testid="download-payment-report-btn"
                >
                  {downloadingReport ? (
                    <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Preparing...</>
                  ) : (
                    <><Download className="h-3.5 w-3.5 mr-1.5" />Download Report</>
                  )}
                </Button>
              </div>
              {paymentHistory.length === 0 ? (
                <p className="text-xs text-muted-foreground">No payments recorded yet.</p>
              ) : (
                <div className="space-y-1.5 max-h-48 overflow-y-auto" data-testid="payment-history-list">
                  {paymentHistory.map((p, index) => (
                    <div key={p.id || index} className="flex items-center justify-between bg-muted/40 rounded-md px-3 py-2 text-sm" data-testid={`payment-history-item-${index}`}>
                      <div className="flex flex-col">
                        <span className="font-semibold text-green-700">{formatCurrency(p.amount)}</span>
                        <span className="text-xs text-muted-foreground">
                          {formatDate(p.date)}{p.payment_mode ? ` · ${p.payment_mode}` : ''}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">Bal: {formatCurrency(p.balance_after ?? 0)}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-red-600"
                          onClick={() => handleDeletePayment(p.id)}
                          disabled={!p.id}
                          title="Remove this payment"
                          data-testid={`delete-payment-${index}`}
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentDialogOpen(false)}>Close</Button>
            <Button onClick={handleSavePayment} disabled={saving} data-testid="save-payment-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Record Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Assign Client Dialog */}
      <Dialog open={bulkAssignDialogOpen} onOpenChange={setBulkAssignDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              Assign POs to Client
            </DialogTitle>
            <DialogDescription>
              Assign {selectedPOs.length} selected purchase order(s) to a client for expense tracking
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Selected POs Summary */}
            <div className="bg-muted/50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Selected POs:</span>
                <Badge variant="secondary">{selectedPOs.length}</Badge>
              </div>
              <div className="text-xs text-muted-foreground max-h-20 overflow-y-auto">
                {filteredOrders
                  .filter(o => selectedPOs.includes(o.id))
                  .map(o => o.po_number)
                  .join(', ')}
              </div>
            </div>

            {/* Client Selection */}
            <div className="space-y-2">
              <Label>Select Client *</Label>
              <Select value={bulkClientId} onValueChange={setBulkClientId}>
                <SelectTrigger data-testid="bulk-client-select">
                  <SelectValue placeholder="Choose a client..." />
                </SelectTrigger>
                <SelectContent>
                  {clients.map(client => (
                    <SelectItem key={client.id} value={client.id}>
                      <div className="flex flex-col">
                        <span>{client.client_name}</span>
                        {client.event_type && (
                          <span className="text-xs text-muted-foreground">{client.event_type}</span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {clients.length === 0 && (
                <p className="text-xs text-amber-600">
                  No clients found. Please create a client first in the Clients & Events page.
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setBulkAssignDialogOpen(false);
              setBulkClientId('');
            }}>Cancel</Button>
            <Button 
              onClick={handleBulkAssignClient} 
              disabled={saving || !bulkClientId} 
              data-testid="confirm-bulk-assign-btn"
            >
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Assign to Client
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create New Item Dialog */}
      <Dialog open={createItemDialogOpen} onOpenChange={setCreateItemDialogOpen}>
        <DialogContent className="!w-[90vw] !max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Create New Item
            </DialogTitle>
            <DialogDescription>
              Create a new item and it will be automatically selected for this PO.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Item Name *</Label>
                <Input
                  value={newItemData.name}
                  onChange={(e) => setNewItemData({ ...newItemData, name: e.target.value })}
                  placeholder="Enter item name"
                  data-testid="new-item-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Item Code</Label>
                <Input
                  value={newItemData.code}
                  onChange={(e) => setNewItemData({ ...newItemData, code: e.target.value })}
                  placeholder="e.g., SKU-001"
                  data-testid="new-item-code"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select
                  value={newItemData.category_id}
                  onValueChange={(value) => setNewItemData({ ...newItemData, category_id: value })}
                >
                  <SelectTrigger data-testid="new-item-category">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Default Unit</Label>
                <Select
                  value={newItemData.default_unit_id}
                  onValueChange={(value) => setNewItemData({ ...newItemData, default_unit_id: value })}
                >
                  <SelectTrigger data-testid="new-item-unit">
                    <SelectValue placeholder="Select unit" />
                  </SelectTrigger>
                  <SelectContent>
                    {units.map(unit => (
                      <SelectItem key={unit.id} value={unit.id}>
                        {unit.name} {unit.symbol && `(${unit.symbol})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Purchase Price (₹)</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={newItemData.purchase_price}
                onChange={(e) => setNewItemData({ ...newItemData, purchase_price: e.target.value })}
                placeholder="0.00"
                data-testid="new-item-price"
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={newItemData.description}
                onChange={(e) => setNewItemData({ ...newItemData, description: e.target.value })}
                placeholder="Optional description..."
                rows={2}
                data-testid="new-item-description"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateItemDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveNewItem} 
              disabled={savingItem || !newItemData.name}
              data-testid="save-new-item-btn"
            >
              {savingItem && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create & Select Item
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PurchaseOrdersPage;
